import React, { useContext } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import clsx from 'clsx';

import { CustomizeDashboard, NoAccess } from '@vyce/core/src/components';
import { WidgetItem } from '@vyce/core/src/types';
import { AppDashboard } from '@vyce/core/src/components/Dashboard/AppDashboard';
import { TimeDashboardModuleContext, TimeModuleContext } from '@vyce/core/src/contexts';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { LiveTimeLocationMap } from '@vyce/core/src/views/time/components';
import { ClockInWidget, ClockOutWidget } from '@vyce/core/src/views/widgets/ClockInOutWidget';
import { WorkersOvertimeWidget } from '@vyce/core/src/views/widgets/WorkersOvertimeWidget';
import { TopPerformingWidget } from '@vyce/core/src/views/widgets/TopPerformingWidget';
import { OnSiteTradesWidget } from '@vyce/core/src/views/widgets/OnSiteTradesWidget';
import { LocationUsersWidget } from '@vyce/core/src/views/widgets/LocationUsersWidget';
import { NotClockedOutWidget } from '@vyce/core/src/views/widgets/NotClockedOutWidget';
import { RecentlyRegisteredWidget } from '@vyce/core/src/views/widgets/RecentlyRegisteredWidget';
import { TeamMembersMissingRateWidget } from '@vyce/core/src/views/widgets/TeamMembersMissingRateWidget';
import { DormantTeamMembersWidget } from '@vyce/core/src/views/widgets/DormantTeamMembersWidget';

import { useTimeDashboardData } from './hooks';
import {
  getRecentlyRegisteredRequest,
  getRecentlyRegisteredSharedData,
  getDormantWorkersRequest,
  deleteAllWorkerShiftsRequest,
} from '../../api/time';

export const TimeDashboardModule = () => {
  const horizontalScrollClasses = useHorizontalScrollStyles();

  const {
    timePermission,
    widgetComponent,
    onOpenUniversalInviteForm,
    inviteButtonComponent,
    fetchDormantWorkers,
  } = useContext(TimeDashboardModuleContext);

  const { mainDashboardFilters, updateMainDashboardFilters } = useContext(TimeModuleContext);

  const {
    isMobile,
    selectedCompanyId,
    locations,
    timeDashboard,
    visibleWidgets,
    noWidgetsAvailable,
    hideWidget,
    name,
    onWidgetsChangeViaSettings,
    onWidgetStatusChange,
    onLayoutChange,
    resetDashboards,
    chatUser,
  } = useTimeDashboardData();

  // Permission filtering happens once, in useTimeDashboardData — see
  // widgetPermissions.ts. getWidget only maps a widget to its component.
  const getWidget = (widget: WidgetItem) => {
    switch (widget.system_name) {
      case 'team_on_site':
        return { component: LiveTimeLocationMap, props: { selectedCompanyId } };
      case 'clocked_in_now':
        return {
          component: ClockInWidget,
          props: {
            mainDashboardFilters,
            selectedCompanyId,
            chatUser,
            locations,
          },
        };

      case 'location_users':
        return {
          component: LocationUsersWidget,
          props: { selectedCompanyId, mainDashboardFilters, chatUser },
        };
      case 'not_clocked_out_widget':
        return {
          component: NotClockedOutWidget,
          props: { selectedCompanyId, locations, mainDashboardFilters, chatUser },
        };
      case 'clocked_out_today':
        return {
          component: ClockOutWidget,
          props: {
            mainDashboardFilters,
            selectedCompanyId,
            chatUser,
            locations,
          },
        };
      case 'top_performing_team_members_last_week':
        return {
          component: TopPerformingWidget,
          props: { selectedCompanyId },
        };
      case 'workers_worked_time_overview':
        return { component: WorkersOvertimeWidget, props: { selectedCompanyId } };
      case 'on_site_trades':
        return { component: OnSiteTradesWidget, props: { selectedCompanyId, mainDashboardFilters } };

      case 'team_members_missing_rate':
        return { component: TeamMembersMissingRateWidget, props: { selectedCompanyId, chatUser } };
      case 'recently_registered_time_users':
        return {
          component: RecentlyRegisteredWidget,
          props: {
            selectedCompanyId,
            mainDashboardFilters,
            moduleType: 'time',
            fetchRecentlyRegisteredWorkers: getRecentlyRegisteredRequest,
            getRecentlyRegisteredSharedData: getRecentlyRegisteredSharedData,
            chatUser,
          },
        };
      case 'dormant_team_members':
        return {
          component: DormantTeamMembersWidget,
          props: {
            selectedCompanyId,
            mainDashboardFilters,
            chatUser,
            // public/v1 default for pay-app; legend-app overrides via context with admin/v1
            fetchDormantWorkers: fetchDormantWorkers ?? getDormantWorkersRequest,
            // same removal request for both apps
            removeDormantWorker: deleteAllWorkerShiftsRequest,
          },
        };

      default:
        return {
          component: widgetComponent,
          props: { widget, redirectUrl: '/time/timelogs', mainDashboardFilters },
        };
    }
  };

  return (
    <>
      <Box
        className={clsx(
          horizontalScrollClasses.blockWrapper,
          horizontalScrollClasses.blockWithHideScroll,
          horizontalScrollClasses.navBarBlock
        )}>
        {!isMobile && (
          <Box display="flex">
            <Typography
              variant="subtitle1"
              style={{ marginRight: 4, fontWeight: 600, whiteSpace: 'nowrap' }}
              color="secondary">
              Hey {name},
            </Typography>
            <Typography variant="subtitle1" style={{ whiteSpace: 'nowrap' }} color="textSecondary">
              here’s your Time dashboard...
            </Typography>
          </Box>
        )}
        <Box display="flex" gridGap={16}>
          {inviteButtonComponent ? (
            React.createElement(inviteButtonComponent)
          ) : (
            <Button
              onClick={onOpenUniversalInviteForm}
              size="small"
              data-tour="invite"
              startIcon={<AiOutlinePlus />}
              variant="contained"
              color="primary">
              Invite New Members
            </Button>
          )}

          {/* Nothing to customise when every widget is filtered out. */}
          {!noWidgetsAvailable && (
            <CustomizeDashboard
              companyId={selectedCompanyId}
              widgetItems={visibleWidgets}
              mainDashboardFiltersFromState={mainDashboardFilters}
              timePermission={timePermission}
              teamPermission={false}
              updateMainDashboardFilters={updateMainDashboardFilters}
              onWidgetsChangeViaSettings={onWidgetsChangeViaSettings}
              resetDashboards={resetDashboards}
            />
          )}
        </Box>
      </Box>

      {noWidgetsAvailable ? (
        <NoAccess
          title="No widgets to show here"
          subtitle="None of the Time dashboard widgets are available with your current permissions. Please contact your company Admin."
        />
      ) : (
        <Box style={{ marginRight: '-24px', marginLeft: '-24px' }}>
          <AppDashboard
            layouts={timeDashboard?.layouts}
            onLayoutChange={onLayoutChange}
            widgets={visibleWidgets}
            getWidget={getWidget}
            onWidgetStatusChange={onWidgetStatusChange}
            onWidgetForbidden={hideWidget}
          />
        </Box>
      )}
    </>
  );
};
